import { Sentiment } from '@conversed/shared/src/central/constants/sentiment';

// Single source of truth for sentiment colours/labels/icons across the Home v2
// Intelligence UI (the sentiment bar, the percentage label, and the User Sentiment
// chip). Change a level's colours here and every consumer updates.
//
// Tailwind classes must be full literal strings (the JIT scanner can't see
// `bg-${hue}-50`), so each level lists its own classes - but they all live in one
// place per level, so a colour change is a single edit.

// Score thresholds (0..1 weighted satisfaction). Shared so the bar and the chip
// always classify a topic the same way.
const NEUTRAL_THRESHOLD = 0.4;
const POSITIVE_THRESHOLD = 0.6;

export const getSentimentLevel = (score: number): Sentiment => {
  if (score < NEUTRAL_THRESHOLD) {
    return Sentiment.Unhappy;
  }
  if (score < POSITIVE_THRESHOLD) {
    return Sentiment.Neutral;
  }
  return Sentiment.Happy;
};

type SentimentDisplay = {
  labelKey: string;
  icon: string;
  // Bar fill (bg-) and the matching percentage label (text-).
  barColorClass: string;
  percentColorClass: string;
  // Pill chip: background + text + border together.
  chipClass: string;
};

export const SENTIMENT_DISPLAY: Record<Sentiment, SentimentDisplay> = {
  [Sentiment.Unhappy]: {
    labelKey: 'sentiment.unhappy',
    icon: 'sentiment_dissatisfied',
    barColorClass: 'bg-red-500',
    percentColorClass: 'text-red-500',
    chipClass: 'bg-red-50 text-red-500 border border-red-200',
  },
  [Sentiment.Neutral]: {
    labelKey: 'sentiment.neutral',
    icon: 'sentiment_satisfied',
    // Light-green fill on the bar (#86efac); softer green-500 for the chip and
    // percent so text stays readable and sits one shade below Happy's emerald.
    barColorClass: 'bg-green-300',
    percentColorClass: 'text-green-500',
    chipClass: 'bg-green-50 text-green-500 border border-green-200',
  },
  [Sentiment.Happy]: {
    labelKey: 'sentiment.happy',
    icon: 'emoji_emotions',
    barColorClass: 'bg-emerald-600',
    percentColorClass: 'text-emerald-600',
    chipClass: 'bg-emerald-50 text-emerald-700 border border-emerald-300',
  },
};
